export interface CloudinaryUploadResponse {
  url: string;
  public_id: string;
  secure_url: string;
}

export interface CloudinaryError {
  message: string;
  error?: unknown;
}

export const uploadImageToCloudinary = async (
  file: File
): Promise<CloudinaryUploadResponse> => {
  const cloudName = import.meta.env.CLOUDINARY_CLOUD_NAME;
  const uploadPreset = import.meta.env.CLOUDINARY_UPLOAD_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error(
      'Cloudinary configuration is missing. Please check your environment variables.'
    );
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'Invalid file type. Please upload a JPEG, PNG, or WebP image.'
    );
  }

  // Validate file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    throw new Error(
      'File size too large. Please upload an image smaller than 10MB.'
    );
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  formData.append('folder', 'lingua-craft/questions');

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to upload image');
    }

    const data = await response.json();

    return {
      url: data.secure_url,
      public_id: data.public_id,
      secure_url: data.secure_url,
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error('Failed to upload image. Please try again.');
  }
};

export const deleteImageFromCloudinary = async (
  publicId: string
): Promise<void> => {
  const cloudName = import.meta.env.CLOUDINARY_CLOUD_NAME;
  const apiKey = import.meta.env.CLOUDINARY_API_KEY;
  const apiSecret = import.meta.env.CLOUDINARY_API_SECRET;

  if (!cloudName || !apiKey || !apiSecret) {
    console.warn('Cloudinary configuration incomplete. Cannot delete image.');
    return;
  }

  try {
    // Note: In a production environment, image deletion should be handled on the backend
    // for security reasons as it requires the API secret
    console.log(`Image deletion requested for public_id: ${publicId}`);
    // For now, we'll just log it. In production, make a request to your backend
    // that can safely handle the Cloudinary deletion with the API secret
  } catch (error) {
    console.error('Failed to delete image from Cloudinary:', error);
  }
};

export const generateCloudinaryUrl = (
  publicId: string,
  options?: {
    width?: number;
    height?: number;
    crop?: string;
    quality?: string;
    format?: string;
  }
): string => {
  const cloudName = import.meta.env.CLOUDINARY_CLOUD_NAME;

  if (!cloudName || !publicId) {
    return '';
  }

  let transformations = '';

  if (options) {
    const transformParts = [];

    if (options.width) transformParts.push(`w_${options.width}`);
    if (options.height) transformParts.push(`h_${options.height}`);
    if (options.crop) transformParts.push(`c_${options.crop}`);
    if (options.quality) transformParts.push(`q_${options.quality}`);
    if (options.format) transformParts.push(`f_${options.format}`);

    if (transformParts.length > 0) {
      transformations = `/${transformParts.join(',')}`;
    }
  }

  return `https://res.cloudinary.com/${cloudName}/image/upload${transformations}/${publicId}`;
};
